/**
 * Post-effects. A fixed, well-ordered chain (so it can be fused into few GPU passes):
 *
 *   scene → mirror/kaleidoscope (UV remap) → feedback/trails → bloom → grade/chromatic/
 *           gradient-map/vignette/grain (one fused pass)
 *
 * Every parameter is a normal ParamDef, so effects get generated UI, presets, MIDI and modulation
 * for free. `enabled` toggles let the governor and users switch whole effects off.
 */
import { toggle, type ParamDef, type ParamValues } from './params';

export interface PostFxDef {
  id: string;
  name: string;
  description: string;
  params: ParamDef[];
  /** Governor tier at which this effect is skipped (higher = dropped earlier). */
  dropAtTier?: number;
}

export const POSTFX: readonly PostFxDef[] = [
  {
    id: 'mirror',
    name: 'Mirror & kaleidoscope',
    description: 'Fold the image: mirror halves/quarters, or a rotating kaleidoscope.',
    params: [
      {
        id: 'mode',
        label: 'Mode',
        type: 'float',
        min: 0,
        max: 4,
        default: 0,
        step: 1,
        control: 'select',
        options: ['Off', 'Mirror left/right', 'Mirror top/bottom', 'Quad', 'Kaleidoscope'],
      },
      { id: 'segments', label: 'Segments', type: 'float', min: 2, max: 16, default: 6, step: 1 },
      { id: 'spin', label: 'Spin', type: 'float', min: -1, max: 1, default: 0.1 },
    ],
  },
  {
    id: 'feedback',
    name: 'Trails / feedback',
    description:
      'Blend in the previous frame, zoomed and rotated: trails, tunnels, MilkDrop warps.',
    dropAtTier: 3,
    params: [
      toggle('enabled', 'Enabled'),
      { id: 'amount', label: 'Amount', type: 'float', min: 0, max: 0.97, default: 0.75 },
      { id: 'zoom', label: 'Zoom', type: 'float', min: 0.9, max: 1.1, default: 1.02 },
      { id: 'rotate', label: 'Rotate', type: 'float', min: -0.1, max: 0.1, default: 0.01 },
      { id: 'hue', label: 'Hue drift', type: 'float', min: 0, max: 0.1, default: 0 },
    ],
  },
  {
    id: 'bloom',
    name: 'Bloom',
    description: 'Bright areas glow.',
    dropAtTier: 2,
    params: [
      toggle('enabled', 'Enabled', 1),
      { id: 'strength', label: 'Strength', type: 'float', min: 0, max: 2, default: 0.5 },
      { id: 'threshold', label: 'Threshold', type: 'float', min: 0, max: 1, default: 0.55 },
      { id: 'radius', label: 'Radius', type: 'float', min: 0.5, max: 3, default: 1.2 },
    ],
  },
  {
    id: 'color',
    name: 'Colour',
    description: 'Brightness, contrast, saturation, hue, and a palette gradient map.',
    params: [
      { id: 'brightness', label: 'Brightness', type: 'float', min: -0.5, max: 0.5, default: 0 },
      { id: 'contrast', label: 'Contrast', type: 'float', min: 0.5, max: 2, default: 1 },
      { id: 'saturation', label: 'Saturation', type: 'float', min: 0, max: 2, default: 1 },
      { id: 'hue', label: 'Hue rotate', type: 'float', min: 0, max: 1, default: 0 },
      { id: 'gradientMap', label: 'Palette map', type: 'float', min: 0, max: 1, default: 0 },
    ],
  },
  {
    id: 'lens',
    name: 'Lens',
    description: 'Chromatic aberration, vignette and film grain.',
    dropAtTier: 1,
    params: [
      { id: 'chromatic', label: 'Chromatic', type: 'float', min: 0, max: 0.02, default: 0 },
      { id: 'vignette', label: 'Vignette', type: 'float', min: 0, max: 1, default: 0.25 },
      { id: 'grain', label: 'Grain', type: 'float', min: 0, max: 0.3, default: 0 },
    ],
  },
];

export function findPostFx(id: string): PostFxDef | undefined {
  return POSTFX.find((f) => f.id === id);
}

/** Effective value of a post-fx parameter (scene override or default). */
export function postFxValue(
  values: Record<string, ParamValues>,
  fxId: string,
  paramId: string,
): number {
  const v = values[fxId]?.[paramId];
  if (typeof v === 'number') return v;
  return findPostFx(fxId)?.params.find((p) => p.id === paramId)?.default ?? 0;
}
